/**
 * AP baseline -- server-state hooks and response types for "Re-apply AP baseline" (OP#1775).
 *
 * Backend: api/v2/ap_baseline.py over services/ap_baseline.py. The baseline converts the TIP
 * factory ROUTED LAN (192.168.1.1/24 + DHCP on LAN*) into bridge mode and drops 6 GHz from
 * Open/WPA2 SSIDs the AP refuses anyway. It is NEVER applied implicitly by SSID or radio pushes
 * -- only by this explicit, audited, per-AP action, one AP at a time (each apply is a ~4 minute
 * outage for that AP).
 *
 *   GET  /devices/{serial}/baseline         status for the device card (read-only, any role)
 *   POST /devices/{serial}/baseline/plan    preview + plan_token (org_admin+, writes nothing)
 *   POST /devices/{serial}/baseline/apply   {plan_token, dry_run, acknowledge_outage,
 *                                            acknowledge_wired_clients: [mac]} (org_admin+)
 *   GET  /sites/{id}/ap-baseline            read-only roll-up: which APs need it
 *
 * The RoleGate in components/device/ApBaselineCard.tsx must match BASELINE_WRITE_ROLE.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { qk } from "./queryKeys";
import { useSession } from "@/stores/session";

/* ---- types ------------------------------------------------------------------------------ */

export type ApBaselineState = "needs_baseline" | "baseline_applied" | "not_supported" | "unknown";
export type ApLanState = "factory_routed" | "bridged" | "custom" | "none" | null;

/** services/ap_baseline.assess() (+ device context on the GET / site roll-up). */
export interface ApBaselineStatus {
  state: ApBaselineState;
  needs_baseline: boolean;
  lan: {
    state: ApLanState;
    bridge_supported: boolean | null;
    unsupported_reason: string | null;
    ports: string[];
  };
  six_ghz: { trim: string[]; six_only: string[] };
  reasons: string[];
  serial?: string;
  name?: string;
  model?: string | null;
  online?: boolean | null;
  config_incompatible?: boolean | null;
  config_uuid?: number | null;
  /** Set while a timed-out baseline push waits for the AP's answer (services/push_confirm). */
  unconfirmed_push?: { at: string | null; command_uuid: string | null; audit_id: number } | null;
}

/** The baseline-field summary of one document (no keys, no PSKs). */
export interface ApBaselineSummary {
  lan_state: ApLanState;
  upstream_ports: string[];
  factory_lan: { name: string | null; ports: string[] }[];
  ssid_bands: Record<string, string[]>;
}

export interface ApBaselineItem {
  code: string;
  message: string;
}

/** One path-level change (secret leaves arrive masked). */
export interface ApBaselineDiffChange {
  op: "add" | "remove" | "change";
  path: string;
  before: unknown;
  after: unknown;
  masked?: boolean;
}

/** A wired client on a LAN port the baseline bridges (services/ap_baseline.wired_clients_on_ports).
 *  It loses its address on apply and must be replugged / power-cycled. */
export interface ApBaselineWiredClient {
  mac: string;
  vendor: string | null;
  ip: string | null;
  hostname: string | null;
  /** "LAN 1", or "LAN 1/LAN 2 (port not reported)" when the firmware cannot pin the port. */
  port: string;
}

/** null = the plan bridges no LAN port. available false = the AP's LAN state could not be read. */
export interface ApBaselineWired {
  available: boolean;
  clients: ApBaselineWiredClient[];
  ports_with_link: string[];
}

export interface ApBaselinePlan {
  serial: string;
  status: ApBaselineStatus;
  changed: boolean;
  notes: string[];
  diff: ApBaselineDiffChange[];
  before: ApBaselineSummary;
  after: ApBaselineSummary;
  config_uuid: number | null;
  plan_token: string;
  refusals: ApBaselineItem[];
  warnings: string[];
  can_apply: boolean;
  outage_minutes: number;
  /** Wired clients the bridge strands (named in the acknowledgement, sent back on apply). */
  wired_clients?: ApBaselineWired | null;
  /** The factory LAN's DHCP lease time ("6h"): how long a stranded client may stay offline. */
  lease_time?: string | null;
  dry_run?: boolean;
  token_matches?: boolean;
}

/** services/push_confirm outcomes an apply can report. */
export type PushConfirmOutcome =
  | "applied"
  | "applied_with_warnings"
  | "pending_confirmation"
  | "rejected"
  | "unknown";

export interface ApBaselineApplyResult {
  serial: string;
  /** False while the AP has not confirmed (pending_confirmation) -- NOT a failure. */
  applied: boolean;
  outcome?: PushConfirmOutcome;
  pending_confirmation?: boolean;
  /** "Waiting for the AP to confirm" while pending. */
  status_text?: string | null;
  reason?: string | null;
  warnings?: string[];
  command_uuid?: string | null;
  notes: string[];
  before: ApBaselineSummary;
  after: ApBaselineSummary;
  previous_config_uuid: number;
  outage_minutes: number;
  wired_clients?: ApBaselineWired | null;
  lease_time?: string | null;
}

export interface SiteApBaseline {
  site_id: number;
  aps: ApBaselineStatus[];
  needs_baseline: number;
  not_supported: number;
}

/* ---- hooks ------------------------------------------------------------------------------- */

/** GET /devices/{serial}/baseline -- the card's status. Read-only (one owgw read). */
export function useApBaselineStatus(serial: string | undefined, enabled = true) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.apBaseline(orgId, serial ?? ""),
    queryFn: ({ signal }) => api.get<ApBaselineStatus>(`/devices/${serial}/baseline`, undefined, signal),
    enabled: !!serial && enabled,
    staleTime: 60_000,
  });
}

/** GET /sites/{id}/ap-baseline -- which APs at the site need it. One owgw read per AP, so it is
 *  cached for 5 minutes and only fetched at site scope. */
export function useSiteApBaseline(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.apBaseline(orgId, siteId),
    queryFn: ({ signal }) => api.get<SiteApBaseline>(`/sites/${siteId}/ap-baseline`, undefined, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });
}

/** POST /devices/{serial}/baseline/plan -- READ-ONLY preview + plan_token. */
export function usePlanApBaseline(serial: string | undefined) {
  return useMutation({
    mutationFn: () => api.post<ApBaselinePlan>(`/devices/${serial}/baseline/plan`, {}),
  });
}

/** POST /devices/{serial}/baseline/apply {dry_run: false} -- PUSHES this one AP.
 *  `wiredMacs` = the plan's wired_clients the operator acknowledged by name; the server
 *  re-reads the LAN ports and refuses (409) if any client on them is not in this list. */
export function useApplyApBaseline(serial: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planToken, wiredMacs }: { planToken: string; wiredMacs: string[] }) =>
      api.post<ApBaselineApplyResult>(`/devices/${serial}/baseline/apply`, {
        plan_token: planToken,
        dry_run: false,
        acknowledge_outage: true,
        acknowledge_wired_clients: wiredMacs,
      }),
    onSuccess: () => {
      // The device card AND the site roll-up (Devices list badge) both end in "ap-baseline".
      void qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey.includes("ap-baseline"),
      });
    },
  });
}
